import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { Card, CardContent } from '../../components/ui/Card';
import ApplicationCard from './components/ApplicationCard';
import ReviewPanel from './components/ReviewPanel';
import WorkflowStats from './components/WorkflowStats';
import FilterPanel from './components/FilterPanel';
import ApprovalNotification from '../../components/ui/ApprovalNotification';
import InviteVendorModal from './components/InviteVendorModal';
import InviteApprovalsPanel from './components/InviteApprovalsPanel';
import QualityApprovalsPanel from './components/QualityApprovalsPanel';
import TablePagination from '../vendor-master-list/components/TablePagination';
import { inviteService } from '../../services/apiService';
import { useToast } from '../../components/ui/Toast';
import { vendorService, approvalService, documentService, getErrorMessage } from '../../services/apiService';
import apiClient from '../../services/apiService';
import { API_BASE_URL, EMAIL_TIMEOUT } from '../../config/api';
import { approverNames } from '../../utils/classification';

const VendorApprovalWorkflow = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showReviewPanel, setShowReviewPanel] = useState(false);
  // 'invites' (invite approval), 'quality' (quality gate), 'level1', 'approved_month', 'rejected'.
  // Approval e-mails deep-link to a tab with ?tab=invites / ?tab=quality.
  const [activeTab, setActiveTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return ['invites', 'quality', 'level1', 'approved_month', 'rejected'].includes(t) ? t : 'level1';
  });
  const [gateCounts, setGateCounts] = useState({ invites: 0, quality: 0 });
  const [qualityRefreshKey, setQualityRefreshKey] = useState(0);
  const [inviteRefreshKey, setInviteRefreshKey] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    country: '',
    sortBy: 'submission_date_desc'
  });
  const [stats, setStats] = useState({
    pendingLevel1: 0,
    approvedMonth: 0,
    rejectedMonth: 0
  });
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });

  // Notification state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState({
    level: 'Level 1',
    vendorName: ''
  });
  const [error, setError] = useState(null);
  const [showInvite, setShowInvite] = useState(false);

  // Helper function to convert vendor object to camelCase (matching AgreementsTab logic)
  const toCamelCaseObject = (obj) => {
    const toCamel = str =>
      str.replace(/_([a-z])/g, (_, char) => char.toUpperCase());

    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [toCamel(key), value])
    );
  };

  // Helper function to fetch digital signature and convert to base64 data URL (matching AgreementsTab logic)
  const fetchDigitalSignature = async (vendorId) => {
    console.log(`[Signature Fetch] Starting signature fetch for vendor ID: ${vendorId}`);

    try {
      // Fetch all documents for the vendor using documentService
      let documents = [];
      try {
        const response = await documentService.getVendorDocuments(vendorId);
        documents = Array.isArray(response) ? response : (response?.data || response?.documents || []);
        console.log(`[Signature Fetch] Found ${documents.length} documents for vendor ${vendorId}`);
      } catch (docError) {
        console.error('[Signature Fetch] Failed to fetch vendor documents:', docError);
        return { success: false, error: 'Failed to fetch documents', data: null };
      }

      // Find the digital signature document
      const digitalSignatureDoc = documents.find(doc => doc.document_type === 'DIGITAL_SIGNATURE');

      if (!digitalSignatureDoc) {
        console.warn(`[Signature Fetch] ⚠️ No DIGITAL_SIGNATURE document found for vendor ${vendorId}`);
        console.log('[Signature Fetch] Available document types:', documents.map(d => d.document_type));
        return { success: false, error: 'No digital signature document found', data: null };
      }

      console.log(`[Signature Fetch] Found digital signature document (ID: ${digitalSignatureDoc.id})`);

      // Try authenticated download first, then fallback to public endpoint
      let blob;
      try {
        const downloadResponse = await documentService.downloadDocument(digitalSignatureDoc.id);
        blob = downloadResponse.data;
        console.log(`[Signature Fetch] ✓ Downloaded signature via authenticated endpoint (size: ${blob.size} bytes)`);
      } catch (authError) {
        console.warn('[Signature Fetch] Authenticated download failed, trying public endpoint:', authError.message);
        // Fallback to public endpoint
        try {
          const downloadResponse = await fetch(`${API_BASE_URL}/documents/${digitalSignatureDoc.id}/public/download`, {
            credentials: 'include',
          });
          if (!downloadResponse.ok) {
            console.error(`[Signature Fetch] ✗ Public endpoint failed with status: ${downloadResponse.status}`);
            return { success: false, error: `Download failed: ${downloadResponse.status}`, data: null };
          }
          blob = await downloadResponse.blob();
          console.log(`[Signature Fetch] ✓ Downloaded signature via public endpoint (size: ${blob.size} bytes)`);
        } catch (publicError) {
          console.error('[Signature Fetch] ✗ Public endpoint error:', publicError);
          return { success: false, error: 'Download failed from both endpoints', data: null };
        }
      }

      // Validate blob
      if (!blob || blob.size === 0) {
        console.error('[Signature Fetch] ✗ Downloaded blob is empty or invalid');
        return { success: false, error: 'Downloaded signature is empty', data: null };
      }

      // Validate blob type (should be an image)
      if (!blob.type.startsWith('image/')) {
        console.warn(`[Signature Fetch] ⚠️ Unexpected blob type: ${blob.type} (expected image/*)`);
      }

      // Convert to blob then to base64 data URL
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result;
          if (!dataUrl || typeof dataUrl !== 'string') {
            console.error('[Signature Fetch] ✗ Failed to convert blob to data URL');
            resolve({ success: false, error: 'Failed to convert to data URL', data: null });
            return;
          }

          // Validate data URL format
          if (!dataUrl.startsWith('data:image/')) {
            console.warn(`[Signature Fetch] ⚠️ Unexpected data URL format: ${dataUrl.substring(0, 30)}...`);
          }

          console.log(`[Signature Fetch] ✓ Successfully converted to data URL (length: ${dataUrl.length} chars)`);
          resolve({ success: true, error: null, data: dataUrl });
        };
        reader.onerror = (error) => {
          console.error('[Signature Fetch] ✗ FileReader error:', error);
          resolve({ success: false, error: 'FileReader error', data: null });
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('[Signature Fetch] ✗ Unexpected error fetching digital signature:', error);
      return { success: false, error: error.message || 'Unknown error', data: null };
    }
  };

  // Helper function to prepare vendor data for agreement generation (matching Generate button logic)
  const prepareVendorDataForAgreements = async (vendor) => {
    console.log(`[Prepare Vendor Data] Preparing data for vendor: ${vendor.company_name} (ID: ${vendor.id})`);

    // Convert vendor object to camelCase
    let vendorData = toCamelCaseObject(vendor);

    // Ensure submission date is available for document date rendering
    vendorData.submissionDate = vendor.created_at || vendor.createdAt || vendor.created_at;

    // Fetch and add digital signature
    const signatureResult = await fetchDigitalSignature(vendor.id);

    if (signatureResult.success && signatureResult.data) {
      vendorData.digitalSignature = signatureResult.data;
      console.log(`[Prepare Vendor Data] ✓ Digital signature added to vendor data`);
    } else {
      vendorData.digitalSignature = null;
      console.warn(`[Prepare Vendor Data] ⚠️ Digital signature NOT available: ${signatureResult.error}`);
    }

    return { vendorData, signatureResult };
  };

  // Fetch vendor applications from API - extracted as reusable function
  const fetchApplications = useCallback(async () => {
    // The invite and quality tabs load their own lists (InviteApprovalsPanel, QualityApprovalsPanel).
    if (activeTab === 'invites' || activeTab === 'quality') {
      setApplications([]);
      setFilteredApplications([]);
      setLoading(false);
      setIsInitialLoad(false);
      return;
    }
    try {
      setLoading(true);

      let vendors = [];
      let totalItems = 0;
      let totalPages = 0;

      if (activeTab === 'level1') {
        // Fetch vendors with pending LEVEL_1 approvals OR LEVEL_1 APPROVED awaiting acknowledgement
        try {
          // Fetch LEVEL_1 PENDING approvals
          const pendingApprovalsResponse = await approvalService.getApprovals({
            level: 'LEVEL_1',
            status: 'PENDING',
            page: pagination.page,
            limit: pagination.limit
          });

          const pendingApprovals = pendingApprovalsResponse?.data || [];
          const meta = pendingApprovalsResponse?.meta || {};

          // Also fetch LEVEL_1 APPROVED approvals that have acknowledgement PENDING or CONFLICTED
          const awaitingAckResponse = await approvalService.getApprovals({
            level: 'LEVEL_1',
            status: 'APPROVED',
            page: pagination.page,
            limit: pagination.limit
          });

          const awaitingAckApprovals = (awaitingAckResponse?.data || []).filter(
            approval => approval.vendor?.acknowledgement?.status === 'PENDING' || 
                       approval.vendor?.acknowledgement?.status === 'CONFLICTED'
          );

          // Combine both sets and remove duplicates
          // Exclude REJECTED vendors - they belong only in the Rejected tab
          const allApprovals = [...pendingApprovals, ...awaitingAckApprovals];
          const uniqueVendors = new Map();
          
          allApprovals.forEach(approval => {
            if (approval.vendor && !approval.vendor.deleted_at && approval.vendor.status?.toUpperCase() !== 'REJECTED') {
              const vendorId = approval.vendor.id;
              if (!uniqueVendors.has(vendorId)) {
                uniqueVendors.set(vendorId, {
                  ...approval.vendor,
                  approval_id: approval.id,
                  approval_level: approval.level,
                  approval_status: approval.status
                });
              }
            }
          });

          vendors = Array.from(uniqueVendors.values());
          
          // Update totals - use pendingApprovals meta for pagination
          totalItems = Math.max(meta.total || vendors.length, vendors.length);
          totalPages = Math.ceil(totalItems / pagination.limit);
        } catch (err) {
          console.error('Error fetching pending LEVEL_1 approvals:', err);
          // Fallback: fetch by status if approval service fails
          const pendingResponse = await vendorService.getVendors({
            status: 'PENDING',
            page: pagination.page,
            limit: pagination.limit
          });
          vendors = pendingResponse.data || [];
          const meta = pendingResponse.meta || {};
          totalItems = meta.total || vendors.length;
          totalPages = meta.totalPages || Math.ceil(totalItems / pagination.limit);
        }
      } else if (activeTab === 'approved_month') {
        // Fetch vendors with LEVEL_2 PENDING approvals (after acknowledgement)
        try {
          const level2ApprovalsResponse = await approvalService.getApprovals({
            level: 'LEVEL_2',
            status: 'PENDING',
            page: pagination.page,
            limit: pagination.limit
          });

          const level2Approvals = level2ApprovalsResponse?.data || [];
          const meta = level2ApprovalsResponse?.meta || {};

          // Extract vendor data from approval records and include approval date
          vendors = level2Approvals
            .filter(approval => approval && approval.vendor && !approval.vendor.deleted_at)
            .map(approval => ({
              ...approval.vendor,
              approval_id: approval.id,
              approval_level: approval.level,
              approval_status: approval.status, // This should be 'PENDING'
              approval_created_at: approval.created_at // Store approval date for filtering
            }));

          totalItems = meta.total || vendors.length;
          totalPages = meta.totalPages || Math.ceil(totalItems / pagination.limit);
        } catch (err) {
          console.error('Error fetching LEVEL_2 PENDING approvals:', err);
          vendors = [];
          totalItems = 0;
          totalPages = 0;
        }
      } else if (activeTab === 'rejected') {
        // Fetch REJECTED vendors with pagination
        const rejectedResponse = await vendorService.getVendors({
          status: 'REJECTED',
          page: pagination.page,
          limit: pagination.limit
        });
        vendors = rejectedResponse.data || [];
        const meta = rejectedResponse.meta || {};
        totalItems = meta.total || vendors.length;
        totalPages = meta.totalPages || Math.ceil(totalItems / pagination.limit);
      }

      // Fetch document counts for each application (only for current page)
      const withDocCounts = await Promise.all(
        vendors.map(async (app) => {
          try {
            const docsResponse = await documentService.getVendorDocuments(app.id);
            const documents = docsResponse?.data || docsResponse || [];
            const documentsCount = Array.isArray(documents) ? documents.length : 0;
            return { ...app, documentsCount };
          } catch (_) {
            return { ...app, documentsCount: 0 };
          }
        })
      );

      setApplications(withDocCounts);

      // Update pagination totals from server response
      setPagination(prev => ({
        ...prev,
        total: totalItems,
        totalPages: totalPages
      }));

      // Refresh stats after fetching applications to ensure counts are up-to-date
      try {
        const statsData = await approvalService.getStats();
        setStats({
          pendingLevel1: statsData?.data?.workflow_pending_by_level?.LEVEL_1 || 
                        statsData?.data?.pending_by_level?.LEVEL_1 || 0,
          approvedMonth: statsData?.data?.approved_month || 0,
          rejectedMonth: statsData?.data?.rejected_month || 0
        });
      } catch (statsErr) {
        // Non-critical - stats refresh failed, continue
        console.warn('Failed to refresh stats:', statsErr);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching applications:', err);
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [activeTab, pagination.page, pagination.limit]);

  // Fetch vendor applications on mount and when pagination/tab changes
  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // Badges for the two gates: invites awaiting approval, suppliers waiting for quality.
  const fetchGateCounts = useCallback(async () => {
    try {
      const [inv, q] = await Promise.all([
        inviteService.getApprovals({ status: 'PENDING_APPROVAL', limit: 1 }),
        approvalService.getApprovals({ level: 'QUALITY', status: 'PENDING', limit: 1 }),
      ]);
      setGateCounts({ invites: inv?.meta?.total ?? 0, quality: q?.meta?.total ?? 0 });
    } catch (err) {
      console.error('Error fetching gate counts:', err);
    }
  }, []);
  useEffect(() => { fetchGateCounts(); }, [fetchGateCounts, activeTab]);
  const onInviteCount = useCallback((n) => setGateCounts((c) => ({ ...c, invites: n })), []);
  const onQualityCount = useCallback((n) => setGateCounts((c) => ({ ...c, quality: n })), []);

  // Auto-refresh data every 30 seconds to catch vendor acknowledgements
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchApplications();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(intervalId);
  }, [fetchApplications]);

  // Fetch workflow statistics from API
  // useEffect(() => {
  //   const fetchWorkflowStats = async () => {
  //     try {
  //       const statsData = await approvalService.getStats();

  //       // Transform backend response to expected format
  //       setStats({
  //         pendingLevel1: statsData?.data?.workflow_pending_by_level?.LEVEL_1 || 
  //                       statsData?.data?.pending_by_level?.LEVEL_1 || 0,
  //         approvedMonth: statsData?.data?.approved_month || 0,
  //         rejectedMonth: statsData?.data?.rejected_month || 0
  //       });
  //     } catch (err) {
  //       console.error('Error fetching workflow stats:', err);
  //       // Fallback to calculated stats from applications
  //       if (applications.length > 0) {
  //         const pendingLevel1 = applications.filter(app => app.status === 'pending' || app.status === 'PENDING_LEVEL_1').length;
  //         const approvedMonth = applications.filter(app => {
  //           const now = new Date();
  //           const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  //           const approvedDate = new Date(app.approved_at || app.created_at);
  //           return (
  //             (app.status === 'approved' || app.status === 'APPROVED') &&
  //             approvedDate >= firstDayOfMonth
  //           );
  //         }).length;
  //         const rejectedMonth = applications.filter(app => {
  //           const now = new Date();
  //           const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  //           const rejectedDate = new Date(app.updated_at || app.created_at);
  //           return (
  //             (app.status === 'rejected' || app.status === 'REJECTED') &&
  //             rejectedDate >= firstDayOfMonth
  //           );
  //         }).length;

  //         setStats({
  //           pendingLevel1,
  //           approvedMonth,
  //           rejectedMonth
  //         });
  //       }
  //     }
  //   };

  //   fetchWorkflowStats();
  // }, [applications]);

  useEffect(() => {
    // Apply client-side filters to the current page of applications
    // Note: Server-side pagination handles the main filtering by tab
    // Client-side filters (search, category, country, sorting) are applied to the current page
    let filtered = [...applications];

    if (filters.search) {
      filtered = filtered.filter(app =>
        app.company_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        app.contact_person_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        app.email?.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    if (filters.category) {
      filtered = filtered.filter(app => app.supplier_category?.toLowerCase() === filters.category.toLowerCase());
    }

    if (filters.country) {
      filtered = filtered.filter(app => app.country_origin === filters.country);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'submission_date_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'submission_date_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'company_name_asc':
          return a.company_name?.localeCompare(b.company_name || '');
        case 'company_name_desc':
          return b.company_name?.localeCompare(a.company_name || '');
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    setFilteredApplications(filtered);
  }, [applications, filters]);

  const handleViewDetails = async (application) => {
    try {
      // Fetch detailed vendor information using vendorService
      const detailedVendor = await vendorService.getVendorById(application.id);

      // Fetch vendor documents
      let documents = [];
      try {
        const docsResponse = await documentService.getVendorDocuments(application.id);
        documents = docsResponse?.data || docsResponse || [];
      } catch (_) {
        // Documents fetch failed, continue with empty array
      }

      // Fetch vendor approval history
      let history = [];
      try {
        const historyResponse = await approvalService.getVendorHistory(application.id);
        history = historyResponse?.data || historyResponse || [];
      } catch (_) {
        // History fetch failed, continue with empty array
      }

      // Fetch document resubmission requests
      let documentRequests = [];
      try {
        const drResponse = await vendorService.getDocumentRequests(application.id);
        documentRequests = drResponse?.data || drResponse || [];
      } catch (_) {
        // Document requests fetch failed, continue with empty array
      }

      // Combine all data
      // IMPORTANT: Preserve approval_status, approval_level, and acknowledgement from original application
      const detailedApplication = {
        ...(detailedVendor?.data || detailedVendor),
        documents: documents,
        document_requests: documentRequests,
        history: history,
        // Preserve approval fields from original application object
        approval_id: application.approval_id,
        approval_level: application.approval_level,
        approval_status: application.approval_status,
        // Preserve acknowledgement data from original application (getVendorById doesn't include it)
        acknowledgement: application.acknowledgement,
        // The quality gate: who may decide it (set by the Quality Approval tab)
        quality_assigned_to_ids: application.quality_assigned_to_ids,
        quality_assignees: application.quality_assignees
      };

      setSelectedApplication(detailedApplication);
      setShowReviewPanel(true);
    } catch (err) {
      console.error('Error fetching vendor details:', err);
      toast.error('Failed to load vendor details');
      // Fallback to basic application data
      setSelectedApplication(application);
      setShowReviewPanel(true);
    }
  };

  // The quality gate, decided from the review popup (Quality Approval tab).
  const handleQualityDecision = async (status, reason) => {
    const app = selectedApplication;
    if (!app) return;
    try {
      await approvalService.submitDecision(app.id, 'QUALITY', status, reason);
      toast.success(status === 'APPROVED'
        ? `${app.company_name} cleared by quality; Level 1 approval is now open`
        : `${app.company_name} rejected at quality`);
      setShowReviewPanel(false);
      setSelectedApplication(null);
      setQualityRefreshKey((k) => k + 1);
      fetchGateCounts();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleApprove = async (applicationId, remarks, questionnaireData = {}, options = {}) => {
    console.log('Approving application:', applicationId, 'with remarks:', remarks, 'questionnaireData:', questionnaireData);

    try {
      // Determine approval level based on current tab and vendor status
      // If in Level 1 tab, use LEVEL_1; if in Level 2 tab, use LEVEL_2; otherwise use FINAL
      let approvalLevel = 'FINAL';
      if (activeTab === 'level1') {
        approvalLevel = 'LEVEL_1';
      } else if (activeTab === 'approved_month') {
        // Level 2 Pending tab
        approvalLevel = 'LEVEL_2';
      }

      // CRITICAL: Call the approval API using approvalService
      // This is the critical operation - if it fails, the entire approval fails
      let approvalResponse;
      try {
        approvalResponse = await approvalService.submitDecision(
          applicationId,
          approvalLevel,
          'APPROVED',
          remarks || 'Approved through process',
          {
            supplier_group: questionnaireData.supplierGroup ?? undefined,
            supplier_category: questionnaireData.supplierCategory ?? undefined,
            commodity_code: questionnaireData.commodityCode ?? undefined,
            supplier_term_of_payment: questionnaireData.supplierTermOfPayment ?? undefined,
            supplier_payment_method: questionnaireData.supplierPaymentMethod ?? undefined,
            supplier_delivery_terms: questionnaireData.supplierDeliveryTerms ?? undefined,
            supplier_mode_of_delivery: questionnaireData.supplierModeOfDelivery ?? undefined
          }
        );

        // Verify the response indicates success
        if (!approvalResponse || (approvalResponse.error && !approvalResponse.success)) {
          throw new Error(approvalResponse?.error?.message || approvalResponse?.message || 'Approval API returned an error');
        }

        console.log('Approval API call succeeded:', approvalResponse);
      } catch (apiError) {
        // This is a critical error - approval failed, so we must stop here
        console.error('CRITICAL: Approval API call failed:', apiError);
        console.error('Error details:', {
          message: apiError.message,
          response: apiError.response?.data,
          status: apiError.response?.status,
          statusText: apiError.response?.statusText
        });
        const errorMsg = getErrorMessage(apiError);
        if (!options.suppressErrorToast) {
          toast.error(`Failed to approve vendor: ${errorMsg}`);
        }
        throw apiError; // Re-throw to prevent showing success message
      }

      // NON-CRITICAL: Generate PDF documents and send Excel email
      // IMPORTANT: Skip for LEVEL_1 - emails sent after vendor acknowledges
      // These operations can fail without blocking the approval
      let emailWarnings = [];
      
      if (approvalLevel !== 'LEVEL_1' && approvalLevel !== 'LEVEL_2') {
        try {
          // Agreements are generated by backend on-demand when sending Excel email
          const generatedDocuments = [];

        // Send Excel email (backend generates agreement PDFs if not in DB)
        // Always include generated_documents array (even if empty) to ensure backend receives it
        try {
          const ccEmails = [

            'karan.nanda@ambergroupindia.com',
            'girishsaluja@ambergroupindia.com',
            'sandeepagarwal@ambergroupindia.com',
            'ashutosh@ambergroupindia.com',
            'rakeshkumar@ambergroupindia.com',
            'sharma.abhishek@ambergroupindia.com',
            'raghvendra.yadav@ambergroupindia.com',
            'pallavroy@ambergroupindia.com',
            'seema.rani@ambergroupindia.com',
            'priyanka.bisht@ambergroupindia.com',
            'riya.srivastav@ambergroupindia.com',
            'anshu.gulia@ambergroupindia.com',
            'priyanka.sharma@ambergroupindia.com',
            'jyotsna@ambergroupindia.com',
            'tarun.kumar@ambergroupindia.com',





            // 'satishshrivastav@ambergroupindia.com',
          ];

          const emailPayload = {
            vendor_id: applicationId,
            questionnaire_data: questionnaireData,
            email_to: 'satishshrivastav@ambergroupindia.com',
            subject: `Vendor Approval - ${selectedApplication?.company_name || 'Vendor'}`,
            cc: ccEmails,
            generated_documents: generatedDocuments // Always include, even if empty array
          };

          console.log(`Sending email with ${generatedDocuments.length} generated agreement(s) and vendor documents`);

          await apiClient.post(`/vendors/${applicationId}/send-excel-email`, emailPayload, {
            timeout: EMAIL_TIMEOUT
          });
          console.log(`✓ Excel email sent successfully (agreements generated by backend if needed)`);
        } catch (emailErr) {
          console.error('✗ NON-CRITICAL: Failed to send Excel email:', emailErr);
          emailWarnings.push(`Failed to send approval email: ${emailErr.message || emailErr}`);
        }
      } catch (excelError) {
        // Non-critical error - log but don't block approval
        console.error('NON-CRITICAL: Error in email/document generation:', excelError);
        emailWarnings.push('Error in email/document generation');
      }
    } else {
      // For LEVEL_1: Acknowledgement email is sent by backend
      // No PDF generation or Excel email needed here
      console.log('Level 1 approval: Skipping PDF generation and Excel email. Acknowledgement email sent by backend.');
    }

      // Immediately remove the approved vendor from local state (optimistic update)
      // This ensures the UI updates immediately even if backend hasn't updated status yet
      setApplications(prev => prev.filter(app => app.id !== applicationId));

      // Reset to first page if current page becomes empty after removal
      setPagination(prev => {
        const remainingTotal = prev.total - 1;
        const newTotalPages = Math.ceil(remainingTotal / prev.limit);
        const newPage = prev.page > newTotalPages && newTotalPages > 0 ? newTotalPages : prev.page;
        return {
          ...prev,
          total: remainingTotal,
          totalPages: newTotalPages,
          page: newPage
        };
      });

      // Close the review panel
      setShowReviewPanel(false);
      setSelectedApplication(null);

      // Refresh workflow stats immediately
      try {
        const statsData = await approvalService.getStats();
        setStats({
          pendingLevel1: statsData?.data?.workflow_pending_by_level?.LEVEL_1 || 
                        statsData?.data?.pending_by_level?.LEVEL_1 || 0,
          approvedMonth: statsData?.data?.approved_month || 0,
          rejectedMonth: statsData?.data?.rejected_month || 0
        });
      } catch (statsError) {
        console.error('Failed to refresh workflow stats:', statsError);
        // Non-critical, continue
      }

      // Show notification
      setNotificationData({
        level: approvalLevel === 'LEVEL_1' ? 'Level 1' : approvalLevel,
        vendorName: selectedApplication?.company_name || 'Vendor'
      });
      setShowNotification(true);

      // Show success message
      const vendorName = selectedApplication?.company_name || 'Vendor';
      if (approvalLevel === 'LEVEL_1') {
        toast.success(
          `${vendorName} approved at Level 1. Acknowledgement email sent to vendor.`,
          { duration: 5000 }
        );
      } else {
        toast.success(
          `${vendorName} has been approved successfully at ${approvalLevel}!${emailWarnings.length > 0 ? ' (with email warnings)' : ''}`,
          { duration: 5000 }
        );
      }

      // Show warnings if emails failed (but approval succeeded) - only for non-Level 1
      if (emailWarnings.length > 0 && approvalLevel !== 'LEVEL_1') {
        console.warn('Email warnings:', emailWarnings);
        emailWarnings.forEach(warning => {
          toast.warning(warning, { duration: 8000 });
        });
      }

      // Refresh applications list from backend after a short delay
      // This ensures the UI reflects the actual backend state after backend has processed the update
      // The delay allows the backend to complete the status update transaction
      // Reset to first page before refetching to ensure we see results
      setTimeout(async () => {
        try {
          setPagination(prev => ({ ...prev, page: 1 }));
          await fetchApplications();
        } catch (refetchError) {
          console.error('Failed to refetch applications after approval:', refetchError);
          // Non-critical - we already updated the UI optimistically
        }
      }, 1000); // 1 second delay to allow backend to process

    } catch (error) {
      // This catch block only handles critical errors (API failures)
      // Non-critical errors (emails) are handled above
      console.error('CRITICAL: Approval failed:', error);
      const errorMsg = getErrorMessage(error);

      // Show error notification
      setNotificationData({
        level: 'Error',
        vendorName: selectedApplication?.company_name || 'Vendor',
        reason: 'Approval failed',
        remarks: errorMsg
      });
      setShowNotification(true);

      toast.error(`Failed to approve vendor: ${errorMsg}`);
    }
  };

  const handleReject = async (applicationId, reason, customReason, remarks, rejectionType) => {
    console.log('Rejecting application:', applicationId, 'reason:', reason, 'remarks:', remarks, 'type:', rejectionType);

    try {
      // Determine approval level based on current tab
      let approvalLevel = 'FINAL';
      if (activeTab === 'level1') {
        approvalLevel = 'LEVEL_1';
      }

      if (rejectionType === 'permanent') {
        // Step 1: Submit rejection decision
        await approvalService.submitDecision(
          applicationId,
          approvalLevel,
          'REJECTED',
          `Rejected: ${reason}${remarks ? ` - ${remarks}` : ''}`
        );

        // Step 2: Send existing rejection email
        try {
          const rejectionReason = `${reason}${remarks ? ` - ${remarks}` : ''}`;
          await apiClient.post(`/vendors/${applicationId}/send-level1-rejection-notification`, {
            rejection_reason: rejectionReason
          });
          console.log('Vendor rejection notification sent successfully');
        } catch (notificationError) {
          console.error('Error sending vendor rejection notification:', notificationError);
        }

        // Step 3: Hard delete vendor data
        await vendorService.permanentDeleteVendor(applicationId);
        console.log('Vendor permanently deleted');

        toast.success('Vendor rejected and deleted permanently');
      } else if (rejectionType === 'rectification') {
        // Step 1: Create rectification invite link
        const rectificationResponse = await vendorService.createRectificationLink(applicationId, {
          additional_remarks: remarks
        });

        // Step 2: Submit rejection decision
        await approvalService.submitDecision(
          applicationId,
          approvalLevel,
          'REJECTED',
          `Rejected for rectification: ${remarks}`
        );

        // Email is sent automatically by backend
        console.log('Rectification link created:', rectificationResponse.data.invite_id);

        toast.success('Rectification link sent to vendor');
      } else {
        // Fallback to original behavior if rejectionType is not provided
        await approvalService.submitDecision(
          applicationId,
          approvalLevel,
          'REJECTED',
          `Rejected: ${reason}${remarks ? ` - ${remarks}` : ''}`
        );

        try {
          const rejectionReason = `${reason}${remarks ? ` - ${remarks}` : ''}`;
          await apiClient.post(`/vendors/${applicationId}/send-level1-rejection-notification`, {
            rejection_reason: rejectionReason
          });
        } catch (notificationError) {
          console.error('Error sending vendor rejection notification:', notificationError);
        }

        toast.info(`${selectedApplication?.company_name || 'Vendor'} has been rejected.`);
      }

      // Update the rejected application status instead of removing it
      // This allows it to appear in the rejected tab
      setApplications(prev => prev.map(app =>
        app.id === applicationId
          ? { ...app, status: 'REJECTED' }
          : app
      ));

      // Reset to first page when status changes (to show in rejected tab)
      setPagination(prev => ({
        ...prev,
        page: 1
      }));

      // Refresh workflow stats
      try {
        const statsData = await approvalService.getStats();
        setStats({
          pendingLevel1: statsData?.data?.workflow_pending_by_level?.LEVEL_1 || 
                        statsData?.data?.pending_by_level?.LEVEL_1 || 0,
          approvedMonth: statsData?.data?.approved_month || 0,
          rejectedMonth: statsData?.data?.rejected_month || 0
        });
      } catch (_) { }

      // Show in-app rejection dialog
      setNotificationData({
        level: 'Rejected',
        vendorName: selectedApplication?.company_name || 'Vendor',
        reason: reason,
        remarks: remarks
      });
      setShowNotification(true);

    } catch (error) {
      console.error('Rejection failed:', error);
      const errorMsg = getErrorMessage(error);
      setNotificationData({
        level: 'Error',
        vendorName: selectedApplication?.company_name || 'Vendor',
        reason: 'Rejection failed',
        remarks: errorMsg
      });
      setShowNotification(true);
      toast.error(`Failed to reject vendor: ${errorMsg}`);
    }
  };

  const handleRequestChanges = async (applicationId, remarks) => {
    console.log('Requesting changes for application:', applicationId, 'remarks:', remarks);

    try {
      // Call the approval API using approvalService
      // Note: For requesting changes, we keep the vendor in LEVEL_1 pending state
      // The backend doesn't support 'pending' status, so we'll need to handle this differently
      // For now, we'll create a LEVEL_1 approval with APPROVED status but add a comment about changes needed
      await approvalService.submitDecision(
        applicationId,
        'LEVEL_1',
        'APPROVED',
        `Changes requested: ${remarks || 'Please review and update required information'}`
      );

      // Update application status in local state
      setApplications(prev => prev.map(app =>
        app.id === applicationId
          ? { ...app, status: 'PENDING_LEVEL_1' }
          : app
      ));

      // Refresh workflow stats
      try {
        const statsData = await approvalService.getStats();
        setStats({
          pendingLevel1: statsData?.data?.workflow_pending_by_level?.LEVEL_1 || 
                        statsData?.data?.pending_by_level?.LEVEL_1 || 0,
          approvedMonth: statsData?.data?.approved_month || 0,
          rejectedMonth: statsData?.data?.rejected_month || 0
        });
      } catch (_) { }

      // Show success message using toast
      toast.success('Successfully requested changes from vendor!');

    } catch (error) {
      console.error('Request changes failed:', error);
      const errorMsg = getErrorMessage(error);
      toast.error(`Failed to request changes: ${errorMsg}`);
    }
  };

  const handleAssignVendorCode = async (vendorCode) => {
    if (!selectedApplication?.id) return;
    try {
      // Backend requires LEVEL_2 approval before assigning vendor code - submit approval first
      await handleApprove(selectedApplication.id, 'Vendor code assigned', {}, { suppressErrorToast: true });
      await vendorService.assignVendorCode(selectedApplication.id, vendorCode || null);
      toast.success(`Vendor code assigned to ${selectedApplication.company_name}`);
      setShowReviewPanel(false);
      setSelectedApplication(null);
    } catch (error) {
      toast.error(`Failed to assign vendor code: ${getErrorMessage(error)}`);
      throw error;
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    // Note: Client-side filters don't reset pagination since they filter the current page
    // If you want server-side filtering, you'd need to reset pagination and refetch
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      category: '',
      country: '',
      sortBy: 'submission_date_desc'
    });
    // Note: Client-side filters don't reset pagination since they filter the current page
  };

  const handleTabChange = (tab) => {
    // Don't do anything if already on this tab
    if (activeTab === tab) {
      // Just scroll to the list
      window.scrollTo({ top: 400, behavior: 'smooth' });
      return;
    }

    setActiveTab(tab);
    // Reset to first page when switching tabs
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  const handleStatClick = (filterType) => {
    handleTabChange(filterType);
    // Scroll to the list
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: Math.max(1, Math.min(newPage, prev.totalPages || 1))
    }));
    // Scroll to top of applications list
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newLimit) => {
    setPagination(prev => {
      const newTotalPages = Math.ceil(prev.total / newLimit);
      return {
        ...prev,
        limit: newLimit,
        totalPages: newTotalPages,
        page: Math.min(prev.page, newTotalPages || 1)
      };
    });
  };

  if (loading && isInitialLoad) {
    return (
      <div className="space-y-5">
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto"></div>
                <p className="mt-4 text-slate-600">Loading applications...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && isInitialLoad) {
    return (
      <div className="space-y-5">
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon name="AlertCircle" size={48} className="text-red-600 mx-auto mb-4" />
                <p className="text-slate-600 mb-4">{error}</p>
                <Button onClick={() => window.location.reload()}>
                  Retry
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Dashboard', path: '/dashboard-overview' },
          { label: 'Vendor Approval Workflow', path: '/vendor-approval-workflow' }
        ]}
      />

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Vendor Approval Workflow
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Review and process vendor applications through the approval pipeline ({pagination.total} total)
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/vendor-master-list')}
            iconName="Building2"
            iconPosition="left"
          >
            View All Vendors
          </Button>
          <Button
            variant="default"
            onClick={() => setShowInvite(true)}
            iconName="UserPlus"
            iconPosition="left"
          >
            Invite Vendor
          </Button>
        </div>
      </div>

      {/* Workflow Statistics */}
      <WorkflowStats
        stats={stats}
        onStatClick={handleStatClick}
        activeFilter={activeTab}
      />



      {/* Tab Navigation */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <div className="border-b border-slate-200">
          <nav className="flex px-6 overflow-x-auto">
            <button
              onClick={() => handleTabChange('invites')}
              data-testid="tab-invites"
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-semibold text-sm whitespace-nowrap transition-colors ${activeTab === 'invites'
                ? 'border-amber-500 text-amber-700'
                : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
            >
              <Icon name="MailCheck" size={18} />
              <span>Invite Approval</span>
              {gateCounts.invites > 0 && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${activeTab === 'invites' ? 'bg-amber-500/10 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                  {gateCounts.invites}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange('quality')}
              data-testid="tab-quality"
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-semibold text-sm whitespace-nowrap transition-colors ${activeTab === 'quality'
                ? 'border-amber-500 text-amber-700'
                : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
            >
              <Icon name="ShieldCheck" size={18} />
              <span>Quality Approval</span>
              {gateCounts.quality > 0 && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${activeTab === 'quality' ? 'bg-amber-500/10 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                  {gateCounts.quality}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange('level1')}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-semibold text-sm whitespace-nowrap transition-colors ${activeTab === 'level1'
                ? 'border-primary text-primary'
                : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
            >
              <Icon name="Clock" size={18} />
              <span>Level 1 Approval</span>
              {stats.pendingLevel1 > 0 && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${activeTab === 'level1'
                  ? 'bg-primary/10 text-primary'
                  : 'bg-slate-100 text-slate-600'
                  }`}>
                  {stats.pendingLevel1}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange('approved_month')}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-semibold text-sm whitespace-nowrap transition-colors ${activeTab === 'approved_month'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
            >
              <Icon name="CheckCircle" size={18} />
              <span>Assign Vendor Code</span>
              {stats.approvedMonth > 0 && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${activeTab === 'approved_month'
                  ? 'bg-green-600/10 text-green-600'
                  : 'bg-slate-100 text-slate-600'
                  }`}>
                  {stats.approvedMonth}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange('rejected')}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-semibold text-sm whitespace-nowrap transition-colors ${activeTab === 'rejected'
                ? 'border-destructive text-destructive'
                : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
            >
              <Icon name="XCircle" size={18} />
              <span>Rejected</span>
              {stats.rejectedMonth > 0 && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${activeTab === 'rejected'
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-slate-100 text-slate-600'
                  }`}>
                  {stats.rejectedMonth}
                </span>
              )}
            </button>
          </nav>
        </div>
      </Card>

      {/* Applications List */}
      <div className="relative">
        {/* Loading overlay - only show when loading but not on initial load */}
        {loading && !isInitialLoad && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mx-auto"></div>
              <p className="mt-2 text-sm text-slate-600">Loading...</p>
            </div>
          </div>
        )}

        {activeTab === 'invites' ? (
          <InviteApprovalsPanel onCountChange={onInviteCount} refreshKey={inviteRefreshKey} onDecided={() => { setInviteRefreshKey((k) => k + 1); fetchGateCounts(); }} />
        ) : activeTab === 'quality' ? (
          <QualityApprovalsPanel currentUser={user} onViewDetails={handleViewDetails} onCountChange={onQualityCount} refreshKey={qualityRefreshKey} />
        ) : filteredApplications.length === 0 ? (
          <Card className="border border-slate-200 shadow-sm bg-white">
            <CardContent className="p-6">
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="FileText" size={40} className="text-slate-400" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">No Applications Found</h3>
                <p className="text-sm text-slate-600 mb-6 max-w-md mx-auto">
                  {Object.values(filters).some(value => value && value !== '')
                    ? 'Try adjusting your filters to see more results.'
                    : 'No vendor applications are currently pending approval.'
                  }
                </p>
                {Object.values(filters).some(value => value && value !== '') && (
                  <Button
                    variant="outline"
                    onClick={handleClearFilters}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-4">
              {filteredApplications.map((application) => (
                <ApplicationCard
                  key={application.id}
                  application={application}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
              <TablePagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalItems={pagination.total}
                itemsPerPage={pagination.limit}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemLabel="applications"
              />
            )}
          </>
        )}
      </div>

      {/* Review Panel Modal */}
      {showReviewPanel && selectedApplication && (
        <ReviewPanel
          application={selectedApplication}
          isAdmin={isAdmin}
          qualityDecision={selectedApplication.approval_level === 'QUALITY' ? {
            canDecide: isAdmin || (selectedApplication.quality_assigned_to_ids || []).includes(user?.id),
            approverNames: approverNames(selectedApplication.quality_assignees, 'an administrator'),
            onDecide: handleQualityDecision,
          } : undefined}
          onClose={() => {
            setShowReviewPanel(false);
            setSelectedApplication(null);
          }}
          onApprove={handleApprove}
          onReject={handleReject}
          onRequestChanges={handleRequestChanges}
          onAssignVendorCode={handleAssignVendorCode}
          onRefresh={() => handleViewDetails(selectedApplication)}
        />
      )}

      {/* Unified Notification / Dialog */}
      <ApprovalNotification
        isVisible={showNotification}
        onClose={() => setShowNotification(false)}
        level={notificationData.level}
        vendorName={notificationData.vendorName}
        reason={notificationData.reason}
        remarks={notificationData.remarks}
        autoHide={false}
      />

      {showInvite && (
        <InviteVendorModal
          isOpen={showInvite}
          onClose={() => setShowInvite(false)}
        />
      )}
    </div>
  );
};

export default VendorApprovalWorkflow;
